import React, {useRef} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import Nav from 'react-bootstrap/Nav';
import Dropdown from 'react-bootstrap/Dropdown';
import NavDropdown from 'react-bootstrap/NavDropdown';
import axios from 'axios';
import {Link, useNavigate} from 'react-router-dom';
import {Tooltip} from '@/components/tooltips';
import {Icon} from '@/components/icons/Icon';
import {useStoreState} from 'easy-peasy';
import {produce} from 'immer';
import useStoryboardsData from '@/views/storyboard/hooks/useStoryboardsData';
import generateStoryboardUrl from '@/routing/utils/generateStoryboardUrl';
import i18n from 'i18n';
import {ModalAlert} from '@/components/alerts';
import {Storyboard} from '@/entities/storyboard';
import {buildURL, buildStoryboardURL} from '@/utils/urlBuilder';

interface StoryboardLinkProps {
    id: number;
    writeAccess: boolean;
    title: string;
    index: number;
    openStoryboardSettingsModal: () => void;
    openStoryboardSharingModal: () => void;
    moveStoryboardLink: (dragIndex: number, hoverIndex: number) => void;
    onStoryboardMove: () => void;
    activeStoryboardIndex: number;
    onStoryboardDelete: (index: number) => void;
    onlyStoryboard: boolean;
}

interface DragItem {
    type: string;
    id: number;
    index: number;
}

const StoryboardLink: React.FC<StoryboardLinkProps> = ({
    id,
    writeAccess,
    title,
    index,
    openStoryboardSettingsModal,
    openStoryboardSharingModal,
    moveStoryboardLink,
    onStoryboardMove,
    activeStoryboardIndex,
    onStoryboardDelete,
    onlyStoryboard,
}) => {
    const navigate = useNavigate();
    const userIsStoryAdmin = useStoreState((state: any) => state.storyboards.userIsStoryAdmin);
    const activeDummyStorySlug = useStoreState((state: any) => state.activeDummyStory.activeDummyStorySlug);
    const activeStoryObject = useStoreState((state: any) => state.activeStoryObject.activeStoryObject);

    const ref = useRef<any>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState<boolean>(false);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const [, drop] = useDrop<DragItem, void, any>({
        accept: 'storyboardLink',
        hover(item: DragItem, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();

            if (!clientOffset) {
                return;
            }

            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveStoryboardLink(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
        drop() {
            onStoryboardMove();
        },
    });

    const [storyboards, _, setStoryboards] = useStoryboardsData();
    const storyboard = storyboards.find((s: Storyboard) => s.id === id);

    const [, drag] = useDrag<DragItem, any, any>({
        type: 'storyboardLink',
        item: {type: 'storyboardLink', id, index},
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const deleteItself = (): void => {
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        setIsDeleting(true);
        try {
            await axios.delete(buildStoryboardURL('storyboardDetails', id));
            onStoryboardDelete(index);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const duplicateItself = async (): Promise<void> => {
        const response = await axios.post(buildURL('cloneStoryboard', {storyboard_id: id}));
        const newStoryboard: Storyboard = response.data;

        const updatedStoryboards = produce(storyboards, (draft) => {
            draft.splice(index + 1, 0, newStoryboard);
        });

        setStoryboards(updatedStoryboards);

        const url = generateStoryboardUrl(newStoryboard, activeDummyStorySlug, activeStoryObject);

        navigate(url);
    };

    if (userIsStoryAdmin) {
        drag(drop(ref));
    }

    let settingsDropdown: React.ReactNode;
    let storyboardLinkClasses = '';

    if (writeAccess) {
        settingsDropdown = (
            <Tooltip content={i18n.t('jt_settings.text')} placement="top">
                <Dropdown id="storyboard-edit-dropdown" className="ml-auto" data-cy="storyboard-edit-toggle">
                    <Dropdown.Toggle className="plain-button storyboard-edit" bsPrefix=" " childBsPrefix=" "
                        style={{color: '#009efb'}} data-cy="storyboard-link-btn">
                        <Icon name="mdi-settings" size="xs" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        <NavDropdown.Item
                            eventKey="1"
                            active={false}
                            onClick={() => openStoryboardSettingsModal()}
                            data-cy="storyboard-settings-edit-btn"
                        >
                            <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                        </NavDropdown.Item>

                        <NavDropdown.Item
                            eventKey="1"
                            active={false}
                            onClick={() => duplicateItself()}
                            data-cy="storyboard-settings-duplicate-btn"
                        >
                            <Icon name="mdi-content-copy" className="mr-2" /> {i18n.t('jt_duplicate.text')}
                        </NavDropdown.Item>

                        <NavDropdown.Item
                            eventKey="1"
                            active={false}
                            onClick={() => openStoryboardSharingModal()}
                            data-cy="storyboard-settings-share-btn"
                        >
                            <Icon name="mdi-share-outline" className="mr-2" /> {i18n.t('jt_sharing.text')}
                        </NavDropdown.Item>

                        {!onlyStoryboard ? (
                            <NavDropdown.Item
                                eventKey="2"
                                active={false}
                                onClick={() => deleteItself()}
                                data-cy="storyboard-settings-delete-btn"
                            >
                                <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                            </NavDropdown.Item>
                        ) : null}
                    </Dropdown.Menu>
                </Dropdown>
            </Tooltip>
        );
        storyboardLinkClasses = 'padded';
    }

    if (activeStoryboardIndex === index) {
        storyboardLinkClasses += ' active';
    }

    const accessTooltip = (): React.ReactNode => {
        if (!storyboard?.relatedUsers || storyboard.relatedUsers.length === 1) {
            return null;
        }

        const visibleUsers = storyboard.relatedUsers.slice(0, 5);
        const notShownUsers = storyboard.relatedUsers.length - visibleUsers.length;

        return (
            <Tooltip
                content={
                    <div>
                        {visibleUsers.map((u) => (
                            <span className="d-block" key={u.id}>
                                {u.username}
                            </span>
                        ))}
                        {notShownUsers ? (
                            <span className="d-block">
                                {`+ ${notShownUsers} more`}
                            </span>
                        ) : null}
                    </div>
                }
                placement="auto"
            >
                <Icon name="mdi-account-multiple" size="sm" className="mr-2 text-info" />
            </Tooltip>
        );
    };

    const toUrl = storyboard ? generateStoryboardUrl(storyboard, activeDummyStorySlug, activeStoryObject) : '#';

    const displayTitle = !title.trim().length ? '-' : title;

    return (
        <Nav.Item
            ref={ref}
            className="position-relative"
            data-cy="storyboard-link"
            id={`story-storyboard-link-${index}`}
        >
            <Link to={toUrl} className={`storyboard-link nav-link ${storyboardLinkClasses}`}>
                {accessTooltip()}
                {displayTitle}
            </Link>

            {settingsDropdown}
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={i18n.t('jt_delete_storyboard_confirmation.text', {title})}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </Nav.Item>
    );
};

export default StoryboardLink; 